import { Badge, Modal } from '@safetyhub/ui';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { getRiskAssessmentDisplay, getManagementActionLabel } from '@safetyhub/shared';
import { SeverityBadge } from './SeverityBadge';
import type { AIAnalysisDetail } from '../../../lib/inspections';
import { renderNotes } from './ActionItemsInput';
import { LegalBasisChip } from '../../../utils/legalBasisFormatter';
import { MODAL_LAYER } from '../../modalLayers';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';

interface PreviousFindingForModal {
  id: string;
  category: string;
  subCategory?: string;
  description: string;
  severity: Severity;
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  foundAt: string;
  resolutionStatus: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  notes?: string;
  resolvedAt?: string;
  aiAnalysisDetail?: AIAnalysisDetail;
  /** 보고서 최종 개선대책 */
  reportMeasure?: string;
  /** 보고서 최종 위험요인 */
  reportRisk?: string;
}

interface PreviousFindingDetailModalProps {
  finding: PreviousFindingForModal;
  onClose: () => void;
  /** Phase N3: 위험성평가 설정 (score 기반 SeverityBadge 렌더링용) */
  riskConfig?: RiskAssessmentConfig | null;
}

/**
 * 이전 미해결 발견사항 상세 모달
 * AI 분석 결과(위험성 평가, 개선대책, 관련법규)를 읽기 전용으로 표시
 */
export function PreviousFindingDetailModal({ finding, onClose, riskConfig }: PreviousFindingDetailModalProps) {
  const detail = finding.aiAnalysisDetail;

  // Esc·body 스크롤 잠금은 base Modal이 소유한다 — 종전의 자체 effect 둘은 걷었다(두 벌이면
  // 잠금이 두 겹이 되고, 자체 Esc는 레지스트리를 몰라 위에 뜬 모달과 함께 닫혔다).

  const isResolved = finding.resolutionStatus === 'COMPLIED';

  const foundDate = new Date(finding.foundAt).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // config 기반 label 실시간 산출
  const riskAssessmentLabels = (() => {
    const ra = detail?.riskAssessment;
    if (!ra) return null;
    if (riskConfig) {
      // 등급 척도 해소는 shared 단일 진입점을 거친다 (riskLevelCriteria 정본 → levelCriteria 폴백).
      const display = getRiskAssessmentDisplay({
        likelihood: ra.likelihood,
        severity: ra.severity,
        riskLevel: ra.riskLevel,
        config: riskConfig,
      });
      return {
        likelihoodLabel: display?.likelihoodLabel || String(ra.likelihood),
        severityLabel: display?.severityLabel || String(ra.severity),
        riskLevelLabel: display?.riskLabel ?? String(ra.riskLevel),
        action: display?.managementAction ?? ra.action,
      };
    }
    return {
      likelihoodLabel: String(ra.likelihood),
      severityLabel: String(ra.severity),
      riskLevelLabel: String(ra.riskLevel),
      action: ra.action,
    };
  })();

  // 위험도 색상 fallback (config 없을 때)
  const getRiskColorFallback = (level: number) => {
    if (level >= 15) return 'text-red-600 bg-red-50';
    if (level >= 8) return 'text-orange-600 bg-orange-50';
    if (level >= 4) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  // 헤더(상태 배지 + 분류)는 base 헤더의 제목 슬롯에 싣는다 — 그러면 ✕와 함께 **고정**되고 본문만
  // 스크롤한다(종전엔 패널 전체가 스크롤해 sticky 헤더로 흉내 냈다). 제목이 노드라 접근 이름은
  // `ariaLabel`로 같은 분류 문자열을 준다.
  // 층: 점검 상세 본문의 이전 미해결 목록에서 열리는 첫 모달이라 root다(종전 `z-[9999]`는 근거 없는 값).
  const categoryLabel = finding.subCategory
    ? `${finding.category} / ${finding.subCategory}`
    : finding.category;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      overlayZClassName={MODAL_LAYER.root}
      ariaLabel={categoryLabel}
      title={
        <span className="block min-w-0">
          <span className="flex items-center gap-2 mb-1">
            <Badge tone={isResolved ? 'emerald' : 'amber'} shape="rounded">
              {isResolved ? '해결됨' : '미해결'}
            </Badge>
            <SeverityBadge likelihoodScore={finding.aiLikelihoodScore} severityScore={finding.aiSeverityScore} config={riskConfig} />
          </span>
          <span className="block font-semibold text-ink-900 truncate">
            {finding.category}
            {finding.subCategory && <span className="text-ink-500 font-normal"> / {finding.subCategory}</span>}
          </span>
        </span>
      }
    >
        {/* 본문 */}
        <div className="space-y-4">
          {/* 설명 — 정제된 위험요인 우선 */}
          <section>
            <h4 className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-1">위험요인</h4>
            <p className="text-sm text-ink-800 leading-relaxed">{finding.reportRisk || finding.description}</p>
          </section>

          {/* 위치 정보 */}
          {detail?.location && (detail.location.area || detail.location.detail) && (
            <section>
              <h4 className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-1">위치</h4>
              <p className="text-sm text-ink-700">
                {detail.location.area}
                {detail.location.area && detail.location.detail && ' - '}
                {detail.location.detail}
              </p>
            </section>
          )}

          {/* 위험성 평가 */}
          {detail?.riskAssessment && riskAssessmentLabels && (
            <section>
              <h4 className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-2">위험성 평가</h4>
              <div className="bg-surface-soft rounded-card p-3 space-y-2">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xs text-ink-500 mb-1">빈도</div>
                    <div className="text-lg font-bold text-ink-800">{detail.riskAssessment.likelihood}</div>
                    <div className="text-xs text-ink-500">{riskAssessmentLabels.likelihoodLabel}</div>
                  </div>
                  <div className="flex items-center justify-center text-ink-400 text-lg font-light">&times;</div>
                  <div>
                    <div className="text-xs text-ink-500 mb-1">강도</div>
                    <div className="text-lg font-bold text-ink-800">{detail.riskAssessment.severity}</div>
                    <div className="text-xs text-ink-500">{riskAssessmentLabels.severityLabel}</div>
                  </div>
                </div>
                <div className="border-t border-line-soft pt-2 text-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getRiskColorFallback(detail.riskAssessment.riskLevel)}`}>
                    위험도 {detail.riskAssessment.riskLevel} ({riskAssessmentLabels.riskLevelLabel})
                  </span>
                </div>
                {riskAssessmentLabels.action && (
                  <div className="border-t border-line-soft pt-2">
                    <div className="text-xs text-ink-500 mb-1">필요 조치</div>
                    <p className="text-sm text-ink-700">{getManagementActionLabel(riskAssessmentLabels.action)}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 개선대책 — 정제 데이터 우선, 없으면 AI 원본 */}
          {(finding.reportMeasure || (detail?.recommendation?.measures && detail.recommendation.measures.length > 0)) && (
            <section>
              <h4 className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-2">개선대책</h4>
              {finding.reportMeasure ? (
                <p className="text-sm text-ink-700 whitespace-pre-line">{finding.reportMeasure}</p>
              ) : (
                <ul className="space-y-1.5">
                  {detail!.recommendation!.measures!.map((measure, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-ink-700">
                      <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-brand-400" />
                      <span>{measure}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* 관련법규 */}
          {detail?.recommendation?.legalBasis && detail.recommendation.legalBasis.length > 0 && (
            <section>
              <h4 className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-2">관련법규</h4>
              <div className="space-y-1">
                {detail.recommendation.legalBasis.map((law, i) => (
                  <LegalBasisChip key={i} law={law} />
                ))}
              </div>
            </section>
          )}

          {/* 메타 정보 */}
          <section className="border-t border-line-soft pt-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
              <span>발견일: {foundDate}</span>
              <span>상태: {isResolved ? '해결됨' : '미해결'}</span>
              {finding.resolvedAt && (
                <span>해결일: {new Date(finding.resolvedAt).toLocaleDateString('ko-KR')}</span>
              )}
            </div>
            {finding.notes && (
              <div className="mt-2 p-2 bg-surface-soft rounded-card-sm text-sm text-ink-600">
                <span className="text-xs text-ink-400 block mb-1">{isResolved ? '조치 사항' : '메모'}</span>
                <div>{renderNotes(finding.notes)}</div>
              </div>
            )}
          </section>
        </div>
    </Modal>
  );
}

export default PreviousFindingDetailModal;
